import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { Gate } from './components/Gate.tsx';
import { ApiContext, ApiRequestError, createClient } from './lib/api.ts';
import { AuthProvider, useAuth } from './lib/auth.tsx';
import { PreferencesProvider } from './lib/preferences.tsx';
import { activeTenant } from './lib/tenant.tsx';
import { router } from './router.tsx';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      /**
       * **A refused session is not retried.** Asking again with the same token that was just rejected
       * cannot succeed, and each attempt is time a person spends looking at a loading screen before the
       * app recovers (F203). Everything else gets its one retry.
       */
      retry: (count: number, error: unknown) =>
        !(error instanceof ApiRequestError && error.status === 401) && count < 1,
      refetchOnWindowFocus: false,
    },
  },
});

function WithApi() {
  const { getToken } = useAuth();
  const api = useMemo(() => createClient(getToken, activeTenant.get), [getToken]);
  return (
    <ApiContext.Provider value={api}>
      <Gate>
        <RouterProvider router={router} />
      </Gate>
    </ApiContext.Provider>
  );
}

const root = document.getElementById('root');
if (!root) throw new Error('#root missing');

createRoot(root).render(
  <StrictMode>
    <PreferencesProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <WithApi />
        </AuthProvider>
      </QueryClientProvider>
    </PreferencesProvider>
  </StrictMode>,
);
